/**
 * Export Utilities
 *
 * Functions for exporting chart data to CSV and PNG formats.
 */

import type { TimeSeries } from '@/simulation/parsers/types';
import type { AggregatedDataPoint, AggregationFrequency } from './aggregation';
import { aggregateTimeSeries, getSuggestedAggregationMethod } from './aggregation';
import type { UnitSystem } from './unitConversion';
import { convertValue, getDisplayUnit } from './unitConversion';

/**
 * CSV export options
 */
interface CSVExportOptions {
  /** Include header row */
  includeHeader?: boolean;
  /** Column separator */
  separator?: string;
  /** Decimal precision */
  precision?: number;
  /** Unit system for conversion */
  unitSystem?: UnitSystem;
}

/**
 * Convert time series to CSV format
 */
export function timeSeriestoCSV(
  timeSeries: TimeSeries,
  options: CSVExportOptions = {}
): string {
  const {
    includeHeader = true,
    separator = ',',
    precision = 4,
    unitSystem = 'SI',
  } = options;

  const variable = timeSeries.variable;
  const unit = getDisplayUnit(variable.units, unitSystem);
  const rows: string[] = [];

  // Header
  if (includeHeader) {
    rows.push(
      ['Date/Time', 'Month', 'Day', 'Hour', 'Minute', `${variable.variableName} [${unit}]`].join(separator)
    );
  }

  // Data rows
  for (const point of timeSeries.data) {
    const ts = point.timestamp;
    const value = convertValue(point.value, variable.units, unitSystem);
    const dateTime = `${String(ts.month)}/${String(ts.day)} ${ts.hour.toString().padStart(2, '0')}:${ts.minute.toString().padStart(2, '0')}`;
    rows.push(
      [dateTime, ts.month, ts.day, ts.hour, ts.minute, value.toFixed(precision)].join(separator)
    );
  }

  return rows.join('\n');
}

/**
 * Convert aggregated data to CSV format
 */
export function aggregatedDataToCSV(
  data: AggregatedDataPoint[],
  variableName: string,
  unit: string,
  options: CSVExportOptions = {}
): string {
  const {
    includeHeader = true,
    separator = ',',
    precision = 4,
    unitSystem = 'SI',
  } = options;

  const displayUnit = getDisplayUnit(unit, unitSystem);
  const rows: string[] = [];

  // Header
  if (includeHeader) {
    rows.push(['Period', `${variableName} [${displayUnit}]`].join(separator));
  }

  // Data rows
  for (const point of data) {
    const value = convertValue(point.value, unit, unitSystem);
    rows.push([point.label, value.toFixed(precision)].join(separator));
  }

  return rows.join('\n');
}

/**
 * Convert multiple time series to CSV format (for comparison charts)
 */
export function multipleTimeSeriesToCSV(
  series: Array<{ name: string; timeSeries: TimeSeries }>,
  frequency: AggregationFrequency,
  options: CSVExportOptions = {}
): string {
  const {
    includeHeader = true,
    separator = ',',
    precision = 4,
    unitSystem = 'SI',
  } = options;

  const rows: string[] = [];

  // Aggregate all series
  const aggregatedSeries = series.map((s) => ({
    name: s.name,
    variable: s.timeSeries.variable,
    data: aggregateTimeSeries(
      s.timeSeries.data,
      frequency,
      getSuggestedAggregationMethod(s.timeSeries.variable.units)
    ),
  }));

  // Build header
  if (includeHeader) {
    const header = ['Period'];
    for (const s of aggregatedSeries) {
      const unit = getDisplayUnit(s.variable.units, unitSystem);
      header.push(`${s.name} [${unit}]`);
    }
    rows.push(header.join(separator));
  }

  // Get all unique labels
  const labelSet = new Set<string>();
  for (const s of aggregatedSeries) {
    for (const point of s.data) {
      labelSet.add(point.label);
    }
  }
  const labels = Array.from(labelSet);

  // Build data rows
  for (const label of labels) {
    const row: (string | number)[] = [label];
    for (const s of aggregatedSeries) {
      const point = s.data.find((p) => p.label === label);
      if (point) {
        const value = convertValue(point.value, s.variable.units, unitSystem);
        row.push(value.toFixed(precision));
      } else {
        row.push('');
      }
    }
    rows.push(row.join(separator));
  }

  return rows.join('\n');
}

/**
 * Download a string as a file
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType: string = 'text/csv'
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export time series data as CSV file
 */
export function exportTimeSeriesCSV(
  timeSeries: TimeSeries,
  filename?: string,
  options?: CSVExportOptions
): void {
  const csv = timeSeriestoCSV(timeSeries, options);
  const name = filename ?? `${timeSeries.variable.variableName.replace(/\s+/g, '_')}.csv`;
  downloadFile(csv, name);
}

/**
 * Export aggregated data as CSV file
 */
export function exportAggregatedCSV(
  data: AggregatedDataPoint[],
  variableName: string,
  unit: string,
  filename?: string,
  options?: CSVExportOptions
): void {
  const csv = aggregatedDataToCSV(data, variableName, unit, options);
  const name = filename ?? `${variableName.replace(/\s+/g, '_')}.csv`;
  downloadFile(csv, name);
}

/**
 * Export chart element as PNG image
 */
export async function exportChartAsPNG(
  chartElement: HTMLElement,
  filename: string = 'chart.png'
): Promise<void> {
  // Use html2canvas-like approach with SVG
  try {
    // Find the SVG element inside the chart
    const svgElement = chartElement.querySelector('svg');
    if (!svgElement) {
      throw new Error('No SVG element found in chart');
    }

    // Clone the SVG
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;

    // Get computed styles and apply them inline
    const computedStyle = getComputedStyle(svgElement);
    clonedSvg.style.backgroundColor = computedStyle.backgroundColor || '#ffffff';

    // Serialize the SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);

    // Create a canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Set canvas size
    const svgRect = svgElement.getBoundingClientRect();
    const scale = 2; // Retina display
    canvas.width = svgRect.width * scale;
    canvas.height = svgRect.height * scale;
    ctx.scale(scale, scale);

    // Create an image from the SVG
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image
        ctx.drawImage(img, 0, 0, svgRect.width, svgRect.height);
        URL.revokeObjectURL(svgUrl);

        // Export as PNG
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Could not create blob'));
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Could not load SVG image'));
      };

      img.src = svgUrl;
    });
  } catch (error) {
    console.error('Failed to export chart as PNG:', error);
    throw error;
  }
}

/**
 * Generate a table HTML for print-friendly view
 */
export function generatePrintableTable(
  data: AggregatedDataPoint[],
  title: string,
  unit: string,
  unitSystem: UnitSystem = 'SI'
): string {
  const displayUnit = getDisplayUnit(unit, unitSystem);

  let html = `
    <div class="print-table">
      <h3>${title}</h3>
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Value (${displayUnit})</th>
          </tr>
        </thead>
        <tbody>
  `;

  for (const point of data) {
    const value = convertValue(point.value, unit, unitSystem);
    html += `
          <tr>
            <td>${point.label}</td>
            <td>${value.toFixed(2)}</td>
          </tr>
    `;
  }

  html += `
        </tbody>
      </table>
    </div>
  `;

  return html;
}

/**
 * Open print preview for dashboard
 */
export function openPrintPreview(dashboardElement: HTMLElement): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print the dashboard');
    return;
  }

  // Clone the dashboard content
  const content = dashboardElement.cloneNode(true) as HTMLElement;

  // Write to print window
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Simulation Results Dashboard</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 20px;
            color: #333;
          }
          .summary-card {
            display: inline-block;
            padding: 16px;
            margin: 8px;
            border: 1px solid #ddd;
            border-radius: 8px;
          }
          .chart-container {
            page-break-inside: avoid;
            margin: 20px 0;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
          }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Simulation Results Dashboard</h1>
        ${content.innerHTML}
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.print();
}
